import { useEffect, useRef, useState } from "react";
import type { Attempt, Me, Venue } from "@coterie/types";
import { api, ApiError, putPhoto } from "./api.ts";
import { locate, type Fix } from "./geo.ts";
import { toJpeg } from "./photo.ts";
import { Braille } from "./Braille.tsx";
import { nearby, search, type Nearby } from "./venues.ts";

type Step = "boot" | "gate" | "home" | "locating" | "venue" | "capture" | "submitted";

const inviteCode = () => new URLSearchParams(location.search).get("code") ?? "";

function CloseIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m22 2-7 20-4-9-9-4Z" />
      <path d="M22 2 11 13" />
    </svg>
  );
}

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [step, setStep] = useState<Step>("boot");
  const [error, setError] = useState<string | null>(null);

  const [code, setCode] = useState(inviteCode);
  const [handle, setHandle] = useState("");

  const [note, setNote] = useState("");
  const [near, setNear] = useState<Nearby[]>([]);
  const [hits, setHits] = useState<Venue[]>([]);
  const [query, setQuery] = useState("");
  const [venue, setVenue] = useState<Venue | null>(null);

  const [blob, setBlob] = useState<Blob | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [recommend, setRecommend] = useState<boolean | null>(null);
  const [hotTake, setHotTake] = useState("");
  const [submitErr, setSubmitErr] = useState<string | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);
  const attempt = useRef<Attempt>({
    id: "",
    startedAt: "",
    photoRetries: 0,
    completed: false,
  });
  const blobRef = useRef<Blob | null>(null);
  const stepRef = useRef<Step>("boot");
  blobRef.current = blob;
  stepRef.current = step;

  useEffect(() => {
    const land = (u: Me) => {
      setMe(u);
      if (inviteCode()) history.replaceState(null, "", "/");
      setStep("home");
    };

    api
      .me()
      .then(land)
      .catch(async (e) => {
        const code = inviteCode();
        if (code) {
          try {
            land(await api.redeem({ code }));
            return;
          } catch (re) {
            setStep("gate");
            if (re instanceof ApiError && re.message === "handle required") return;
            setError(re instanceof Error ? re.message : "redeem failed");
            return;
          }
        }
        setStep("gate");
        if (!(e instanceof ApiError && e.status === 401)) setError(e.message);
      });
  }, []);

  useEffect(() => {
    const flush = () => {
      const a = attempt.current;
      if (!a.id || a.completed) return;
      const s = stepRef.current;
      if (s === "capture" && !blobRef.current) return; // likely in the native camera
      if (s !== "locating" && s !== "venue" && s !== "capture") return;
      const abandonedAtStep = s === "capture" ? (blobRef.current ? "submit" : "capture") : s;
      void api.attempt({ ...a, completed: false, abandonedAtStep }, true).catch(() => {});
    };
    addEventListener("pagehide", flush);
    return () => removeEventListener("pagehide", flush);
  }, []);

  useEffect(() => {
    if (!query.trim()) {
      setHits([]);
      return;
    }
    setHits(search(query));
  }, [query]);

  const startAttempt = () => {
    attempt.current = {
      id: crypto.randomUUID(),
      startedAt: new Date().toISOString(),
      photoRetries: 0,
      completed: false,
    };
  };

  const redeem = async () => {
    setError(null);
    try {
      const u = await api.redeem({ code, handle });
      setMe(u);
      history.replaceState(null, "", "/");
      setStep("home");
    } catch (e) {
      setError(e instanceof Error ? e.message : "redeem failed");
    }
  };

  const begin = async () => {
    if (attempt.current.id && !attempt.current.completed) {
      void api.attempt({ ...attempt.current, completed: false, abandonedAtStep: "submit" }).catch(() => {});
    }
    setError(null);
    setVenue(null);
    setNear([]);
    setQuery("");
    setHits([]);
    setBlob(null);
    setPreview(null);
    setRecommend(null);
    setHotTake("");
    setSubmitErr(null);
    startAttempt();
    setStep("locating");
    const result = await locate();
    if (stepRef.current !== "locating") return;
    if ("error" in result) {
      attempt.current.gpsAccuracy = undefined;
      setNote(
        result.error === "denied"
          ? "location is off — search for where you are"
          : "couldn't get a fix — search for where you are",
      );
      setNear([]);
    } else {
      attempt.current.gpsAccuracy = result.accuracy;
      const n = nearby(result);
      setNear(n.list);
      setNote(n.note);
      if (n.preselect) {
        setVenue(n.preselect);
        attempt.current.venueSelectedVia = "proximity";
      }
    }
    setStep("venue");
  };

  const pick = (v: Venue, via: "proximity" | "search") => {
    setVenue(v);
    attempt.current.venueSelectedVia = via;
    setStep("capture");
  };

  const commitSearch = () => {
    const v = hits[0];
    if (!v) return;
    pick(v, "search");
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    try {
      const jpeg = await toJpeg(file);
      setBlob(jpeg);
      setPreview((old) => {
        if (old) URL.revokeObjectURL(old);
        return URL.createObjectURL(jpeg);
      });
    } catch {
      setError("couldn't read that photo — try again");
    }
  };

  const uploadWithRetry = async (b: Blob) => {
    let last: unknown;
    for (let i = 0; i < 3; i++) {
      try {
        const { key, url } = await api.uploadUrl();
        await putPhoto(url, b);
        return key;
      } catch (e) {
        last = e;
        attempt.current.photoRetries += 1;
      }
    }
    throw last instanceof Error ? last : new Error("upload failed");
  };

  const submit = () => {
    const v = venue;
    const b = blob;
    const rec = recommend;
    if (!v || !b || rec === null) return;
    setSubmitErr(null);
    setStep("submitted");
    void (async () => {
      try {
        const photoKey = await uploadWithRetry(b);
        await api.review({
          venueId: v.id,
          recommend: rec,
          photoKey,
          ...(hotTake.trim() ? { hotTake: hotTake.trim() } : {}),
        });
        attempt.current.completed = true;
        void api.attempt({ ...attempt.current, completed: true }).catch(() => {});
      } catch {
        setSubmitErr("didn't go through — photo is still here, tap to retry");
      }
    })();
  };

  const retry = () => {
    setSubmitErr(null);
    submit();
  };

  if (step === "boot") {
    return (
      <div className="shell spot">
        <Braille />
      </div>
    );
  }

  if (step === "gate") {
    return (
      <div className="shell">
        <h1>Coterie</h1>
        <p className="lede">invite only.</p>
        <div className="stack">
          <label>
            invite code
            <input
              type="text"
              value={code} autoCapitalize="off" autoCorrect="off" onChange={(e) => setCode(e.target.value)} />
          </label>
          <label>
            handle
            <input
              type="text"
              value={handle}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder="3–20, a-z 0-9 _"
              onChange={(e) => setHandle(e.target.value)}
            />
          </label>
          {error && <p className="err">{error}</p>}
          <button className="primary" onClick={() => void redeem()} disabled={!code || handle.trim().length < 3}>
            enter
          </button>
        </div>
      </div>
    );
  }

  if (step === "home") {
    return (
      <div className="shell spot">
        <p className="spot-brand">coterie</p>
        <a className="spot-me" href="/me">
          @{me?.handle}
        </a>
        <button className="fab" onClick={() => void begin()}>
          review
        </button>
      </div>
    );
  }

  if (step === "locating") {
    return (
      <div className="shell spot">
        <Braille />
      </div>
    );
  }

  if (step === "venue") {
    return (
      <div className="shell">
        <h1>where are you?</h1>
        {note && <p className="note">{note}</p>}
        {near.length > 0 && (
          <ul className="list">
            {near.map((v) => (
              <li key={v.id}>
                <button onClick={() => pick(v, "proximity")}>
                  {v.name}
                  <span className="sub">
                    {v.street || " "} · {Math.round(v.m)}m
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <label>
          search
          <div className="search">
            <input
              type="text"
              value={query}
              placeholder="name or street"
              autoCorrect="off"
              enterKeyHint="go"
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && commitSearch()}
            />
            <button type="button" aria-label="use top result" disabled={hits.length === 0} onClick={commitSearch}>
              →
            </button>
          </div>
        </label>
        {query.trim() && hits.length === 0 && (
          <p className="note">not in the list — that's on us. pick the closest match, or skip.</p>
        )}
        {hits.length > 0 && (
          <ul className="list">
            {hits.map((v) => (
              <li key={v.id}>
                <button onClick={() => pick(v, "search")}>
                  {v.name}
                  <span className="sub">{v.street || " "}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  if (step === "capture") {
    const shutter = (
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => void onFile(e.target.files?.[0])}
      />
    );

    if (!preview) {
      return (
        <div className="shell spot">
          {shutter}
          <button className="spot-who" onClick={() => setStep("venue")}>
            ← {venue?.name}
          </button>
          <button className="fab" onClick={() => fileRef.current?.click()}>
            take photo
          </button>
          {error && <p className="err">{error}</p>}
        </div>
      );
    }

    return (
      <div className="shell compose">
        {shutter}
        <button className="ghost" onClick={() => setStep("venue")}>
          ← {venue?.name}
        </button>
        <div className="preview-wrap">
          <img className="preview" src={preview} alt="" />
          <button className="preview-x" aria-label="retake" onClick={() => fileRef.current?.click()}>
            <CloseIcon />
          </button>
        </div>
        {error && <p className="err">{error}</p>}
        <div className="recommend">
          <p className="q">recommend {venue?.name} to the network?</p>
          <div className="binary">
            <button aria-pressed={recommend === true} onClick={() => setRecommend(true)}>
              {recommend === true && <CheckIcon />}
              yes
            </button>
            <button aria-pressed={recommend === false} onClick={() => setRecommend(false)}>
              {recommend === false && <CloseIcon />}
              no
            </button>
          </div>
        </div>
        <label>
          <span className="field-head">
            hot take
            <span className="count">{hotTake.length}/140</span>
          </span>
          <textarea
            maxLength={140}
            value={hotTake}
            placeholder="optional"
            onChange={(e) => setHotTake(e.target.value)}
          />
        </label>
        <button className="primary" disabled={!blob || recommend === null} onClick={submit}>
          <SendIcon />
          finish review
        </button>
      </div>
    );
  }

  if (submitErr) {
    return (
      <div className="shell">
        <h1>almost</h1>
        <p className="lede">{submitErr}</p>
        <button className="primary" onClick={retry}>
          retry
        </button>
        <button className="ghost" onClick={() => void begin()}>
          review another
        </button>
      </div>
    );
  }

  return (
    <div className="shell spot done">
      <p className="done-copy">logged. well done! carry on.</p>
      <button className="primary done-cta" onClick={() => void begin()}>
        review another
      </button>
    </div>
  );
}
